import { useMemo } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { radioFilterId, timeRangeFilterId } from '../../filters';

const title = t`Radio Transmitted Management Frames`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'radio',
        metrics: ['txMgmtFrameCount'],
    },
};

export function useChartConfig() {
    const color = useMetricColor('serverNetOut');

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: t`Frames`,
                    },
                },
            ],
            tooltip: BuiltInTooltip,
            showGranularity: true,
            statusMetric: 'channelUtilizationStatus',
            chartTrends: [
                {
                    formatter: FORMATTER_TYPES.LOG,
                    units: ' Frames',
                    chartType: 'areaspline',
                    key: 'txMgmtFrameCount',
                    label: t`Transmitted Management Frames`,
                    color,
                    multiplier: 1,
                },
            ],
        }),
        [color]
    );
}

function RadioManagementFramesPanel() {
    const chartConfig = useChartConfig();
    const data = usePanelDataService(dataSource);
    const chartData = data?.data?.[0];

    if (!data) return null;

    return <PerformanceChart chartDef={chartConfig} data={chartData} />;
}

RadioManagementFramesPanel.propTypes = panelPropTypes;

RadioManagementFramesPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'RadioMgmtFramesChart',
    catalogEntry: {
        category: PANEL_CATEGORIES.RADIO,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Transmitted management frames trend for a selected radio`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, radioFilterId],
        optional: [],
    },
})(RadioManagementFramesPanel);
